import { t } from '@hana/i18n';
import './SupplierPay.i18n';
import { useMemo, useState } from 'react';
import { IconAlarm, IconAlertTriangle, IconChartBar, IconCircleCheck, IconDots, IconFileText, IconFlag, IconHistory, IconLock, IconPlus, IconPointer, IconSearch, IconSend } from '@tabler/icons-react';
import { useList } from '@hana/data';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Button, Skeleton, StateView, useToast } from '@hana/ui';
import { financeScreenState, useFinanceAccess } from './access';
import { COLLECTIONS, DUE_FILTERS, PAID_ELSEWHERE, buildGroups, calendar, colDueIds, colSelectable, colStatus, dueText, pendingDocs, topPartners, type DueFilter } from './payables';
import type { PartnerBank, SupplierDoc, SupplierPartner, SupplierPayment } from './types';
import { f1 } from './view';
import { RoeTab } from './RoeTab';
import { PayTab } from './PayTab';
import { SoaTab } from './SoaTab';
import { ProfitTab } from './ProfitTab';
import s from './supplierPay.module.css';

// Mint-SupplierPay (1536) › tab Đợt chi: 6 số · lịch chi theo hạn | chờ chi theo đối tác · đề xuất đợt chi theo đối tác · thu hộ cần chuyển trả (COL).
// TIỀN → sau quyền `money`. Chỉ đọc; nút ghi báo toast.

type Tab = 'batch' | 'pay' | 'soa' | 'profit' | 'roe';
type Per = 'm' | 'q' | 'y';
const PERIODS: readonly (readonly [Per, string])[] = [['m', 'Tháng 10'], ['q', 'Quý 4'], ['y', 'Năm 2026']];
const BANK_CHIP: Record<PartnerBank, readonly [string, string]> = {
  ok: ['TK đã xác minh', 'ok'], changed: ['TK vừa đổi', 'err'], unverified: ['TK chưa xác minh', 'warn'], locked: ['Đối tác tạm khoá chi', 'err'], shared: ['TK dùng chung', 'warn'],
};
const DOC_TYPE = (t: string) => (t === 'Statement of Account' ? 'SOA' : t === 'Other payment request' ? 'RPM' : t === 'OpsCost' ? 'ODN' : 'DBN');
const PLACEHOLDER: Record<Tab, string> = { batch: 'Đối tác / chứng từ / job / MBL', pay: 'Số phiếu / đối tác / nội dung', soa: 'Chứng từ / job', profit: 'Sale / khách / job', roe: 'Ngày' };
const COL_TONE = { over: 'err', due: 'warn', later: 'gray', soa: 'pri', sent: 'ok' } as const;

export function SupplierPay() {
  const toast = useToast();
  const access = useFinanceAccess();
  const [tab, setTab] = useState<Tab>(() => {
    const t = new URLSearchParams(typeof location === 'undefined' ? '' : location.search).get('tab');
    return t === 'pay' || t === 'soa' || t === 'profit' || t === 'roe' ? t : 'batch';
  });
  const [per, setPer] = useState<Per>('m');
  const [q, setQ] = useState('');
  const [fil, setFil] = useState<DueFilter>('all');
  const [sel, setSel] = useState<Record<string, boolean>>({});
  const [colSel, setColSel] = useState<Record<string, boolean>>({});
  const [moreOpen, setMoreOpen] = useState(false);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [closed, setClosed] = useState<ReadonlySet<string>>(new Set());
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });

  const docs = (useList('finSupplierDoc', { limit: 500 }).data?.items ?? []) as readonly SupplierDoc[];
  const partners = (useList('finSupplierPartner', { limit: 500 }).data?.items ?? []) as readonly SupplierPartner[];
  const pays = (useList('finSupplierPayment', { limit: 500 }).data?.items ?? []) as readonly SupplierPayment[];
  const state = financeScreenState(access);
  const waitN = pays.filter((p) => p.state === 'wait').length;

  const groups = useMemo(() => buildGroups(docs, partners, fil, q, closed), [docs, partners, fil, q, closed]);
  const pend = useMemo(() => pendingDocs(docs, closed), [docs, closed]);
  const cal = calendar(pend);
  const top = topPartners(pend);
  const nBlocked = groups.filter((g) => g.bank !== 'ok' && g.bank !== 'shared').length;
  const kpis = [
    ['Chờ chi', String(pend.length), `${f1(pend.reduce((a, d) => a + d.amount, 0))} tr ₫`, ''],
    ['Quá hạn', String(pend.filter((d) => d.due === 'over').length), 'chứng từ', s.err],
    ['Đến hạn ≤ 7 ngày', String(pend.filter((d) => d.due === 'soon').length), 'chứng từ', s.warn],
    ['Phiếu chờ duyệt', String(waitN), 'phiếu chi', ''],
    ['Đã chi tháng', '326', 'phiếu chi · tới 22/Oct', s.okc],
    ['Bị chặn', String(nBlocked), 'Đối tác chưa chi được', s.err],
  ] as const;
  const picked = groups.filter((g) => sel[g.key] && !g.blocked);
  const colPicked = COLLECTIONS.filter((c) => colSel[c.id] && colSelectable(c));
  const selAmt = picked.reduce((a, g) => a + g.amount, 0) + colPicked.reduce((a, c) => a + c.amount, 0);
  const colOpen = COLLECTIONS.filter(colSelectable);
  const colOver = colOpen.filter((c) => c.state === 'over').length;

  const TABS = [
    { key: 'batch' as Tab, label: 'Đợt chi', n: docs.length },
    { key: 'pay' as Tab, label: 'Phiếu chi', n: waitN },
    { key: 'soa' as Tab, label: 'SOA đối tác' },
    { key: 'profit' as Tab, label: 'Chia lợi nhuận' },
  ];
  const ready = state === 'ready';

  return (
    <Page kind="table">
      <PageHeader
        title="Phải trả"
        findHint={false}
        tabs={
          <div className={s.tabs} role="tablist" aria-label="Mục thanh toán">
            {TABS.map((x) => (
              <button key={x.key} type="button" role="tab" aria-selected={tab === x.key} onClick={() => setTab(x.key)}>
                {x.label}
                {ready && x.n !== undefined && <span className={x.key === 'batch' || x.key === 'pay' ? s.hot : ''}>{x.n}</span>}
              </button>
            ))}
            <span className={s.more}>
              <button type="button" aria-haspopup="menu" aria-expanded={moreOpen} aria-selected={tab === 'roe'} onClick={() => setMoreOpen(!moreOpen)}>
                {tab === 'roe' ? 'Tỷ giá' : 'Thêm'} ▾
              </button>
              {moreOpen && (
                <div className={s.menu} role="menu" aria-label="Mục khác">
                  <button type="button" role="menuitemradio" aria-checked={tab === 'roe'} onClick={() => { setTab('roe'); setMoreOpen(false); }}>{t('spy.1')}</button>
                </div>
              )}
            </span>
          </div>
        }
        actions={
          <>
            <HeaderTool label="Duyệt · Việc · Phân tích" icon={IconChartBar} onClick={() => toast({ msg: 'Bảng phân tích chưa mở ở bản này' })} />
            <HeaderTool label="Thao tác khác" icon={IconDots} onClick={noWrite} />
            <Button variant="primary" icon={tab === 'pay' ? <IconPlus size={16} aria-hidden="true" /> : <IconSend size={16} aria-hidden="true" />} onClick={noWrite}>{tab === 'pay' ? 'Phiếu chi' : tab === 'soa' ? 'Gửi đối chiếu' : tab === 'profit' ? 'Tính lại costing' : 'Gửi đợt chi duyệt'}</Button>
          </>
        }
      />
      <div className={s.page}>
        <StateView state={state} skeleton={<Skeleton rows={9} />}>
          <div className={s.bar}>
            <label className={s.search}>
              <IconSearch size={16} aria-hidden="true" />
              <input aria-label="Tìm" placeholder={PLACEHOLDER[tab]} value={q} onChange={(e) => setQ(e.target.value)} />
            </label>
            <div className={s.seg} role="group" aria-label="Kỳ">
              {PERIODS.map(([k, l]) => (
                <button key={k} type="button" aria-pressed={per === k} onClick={() => setPer(k)}>{l}</button>
              ))}
            </div>
          </div>
          {tab === 'roe' ? (
            <RoeTab q={q} />
          ) : tab === 'pay' ? (
            <PayTab pays={pays} partners={partners} q={q} approver="PHILIP" onOpen={() => toast({ msg: 'Ngăn chi tiết phiếu chi chưa mở ở bản này' })} />
          ) : tab === 'soa' ? (
            <SoaTab q={q} per={per} />
          ) : tab === 'profit' ? (
            <ProfitTab q={q} per={per} />
          ) : (
            <>
              <div className={s.kpis}>
                {kpis.map(([k, v, u, tone]) => (
                  <section key={k} className={s.kpi}>
                    <div>{k}</div>
                    <b className={tone}>{v}</b>
                    <div>{u}</div>
                  </section>
                ))}
              </div>
              <div className={s.two}>
                <section className={s.card} aria-label="Lịch chi">
                  <div className={s.ch}>
                    <h2>{t('spy.2')}</h2>
                    <span className={s.leg}>
                      {([['Quá hạn', 'over'], ['≤ 7 ngày', 'soon'], ['Sau đó', 'later']] as const).map(([k, t]) => (
                        <span key={k}><i className={s[`d_${t}`]} />{k}</span>
                      ))}
                      <span>tr ₫</span>
                    </span>
                  </div>
                  <div className={s.cal} role="img" aria-label={`Lịch chi theo hạn (tr ₫): ${cal.map((x) => `${x.k} ${f1(x.v)} (${x.n} chứng từ)`).join(', ')}`}>
                    {cal.map((a) => (
                      <div key={a.k} className={s.calc}>
                        <span className={s.calv}>{a.v ? f1(a.v) : '—'}</span>
                        <div className={`${s.bar2} ${s[`d_${a.tone}`]}`} style={{ height: a.h }} title={`${a.k} · ${a.v ? f1(a.v) : '—'} tr · ${a.n} chứng từ`} />
                        <span className={s.call}>{a.k}</span>
                      </div>
                    ))}
                  </div>
                </section>
                <section className={s.card} aria-label="Chờ chi theo đối tác">
                  <div className={s.ch}>
                    <h2>{t('spy.3')}</h2>
                    <span className={s.leg}>tr ₫</span>
                  </div>
                  <div role="group" aria-label={`Chờ chi theo đối tác (tr ₫): ${top.map((x) => `${x.k} ${f1(x.v)}`).join(', ')}`}>
                    {top.map((a) => (
                      <button key={a.k} type="button" className={s.top} aria-label={`Lọc ${a.k} · ${f1(a.v)} tr`} onClick={() => { setQ(a.k); setFil('all'); }}>
                        <span>{a.k}</span>
                        <span className={s.track}><span className={a.over ? s.d_over : s.d_data} style={{ width: `${a.w}%` }} /></span>
                        <b>{f1(a.v)}</b>
                      </button>
                    ))}
                  </div>
                </section>
              </div>
              <section className={s.card} aria-label="Đề xuất đợt chi theo đối tác">
                <div className={s.bh}>
                  <h2 title="Gom chứng từ đã duyệt (Approved / RQ Payment), ưu tiên quá hạn và đến hạn · mỗi đối tác một phiếu chi">{t('spy.4')}</h2>
                  <div className={s.seg} role="group" aria-label="Lọc hạn">
                    {DUE_FILTERS.map(([k, l]) => (
                      <button key={k} type="button" aria-pressed={fil === k} onClick={() => setFil(k)}>{l}</button>
                    ))}
                  </div>
                </div>
                {groups.map((g) => {
                  const on = !!sel[g.key] && !g.blocked;
                  const [vk, vt] = BANK_CHIP[g.bank];
                  return (
                    <div key={g.key} className={s.grp}>
                      <div className={`${s.gh} ${on ? s.gon : ''}`}>
                        <input type="checkbox" aria-label={`Chọn ${g.key}`} checked={on} disabled={g.blocked} title={g.blockNote || undefined} onChange={() => setSel({ ...sel, [g.key]: !sel[g.key] })} />
                        <div className={s.gn}><b>{g.key}</b><div>{g.name}</div></div>
                        <b className={s.gamt}>{f1(g.amount)} tr</b>
                        <span className={s.gdoc} title={`${g.docs.length} chứng từ · ${g.over ? `${g.over} quá hạn` : 'trong hạn'}`}>
                          <span aria-label={`${g.docs.length} chứng từ`}><IconFileText size={13} aria-hidden="true" />{g.docs.length}</span>
                          {g.over > 0 && <span className={s.err} aria-label={`${g.over} quá hạn`}><IconAlertTriangle size={13} aria-hidden="true" />{g.over}</span>}
                        </span>
                        <span className={`${s.vchip} ${s[`v_${vt}`]}`}>{vk}</span>
                        <span className={s.fill} />
                        {g.needVerify && (
                          <button type="button" className={s.mini} onClick={noWrite}><IconCircleCheck size={16} aria-hidden="true" />{t('spy.5')}</button>
                        )}
                        <button type="button" className={s.hist} aria-label={`Lịch sử thanh toán ${g.key}`} title="Lịch sử thanh toán" onClick={() => toast({ msg: 'Sổ thu chi chưa mở ở bản này' })}>
                          <IconHistory size={15} aria-hidden="true" />
                        </button>
                        <button type="button" className={s.exp} aria-expanded={!!open[g.key]} aria-label={`Xem chứng từ ${g.key}`} onClick={() => setOpen({ ...open, [g.key]: !open[g.key] })}>
                          {open[g.key] ? '▾' : '▸'}
                        </button>
                      </div>
                      {open[g.key] && (
                        <div className={s.docs}>
                          <div className={`${s.dg} ${s.dgh}`}><span>{t('spy.6')}</span><span>Job</span><span>MBL</span><span>{t('spy.7')}</span><span>{t('spy.8')}</span><span>{t('spy.9')}</span><span className={s.r}>{t('spy.10')}</span></div>
                          {g.docs.map((d) => (
                            <div key={d.id} className={s.dg}>
                              <b className={s.link}>{d.id}</b><button type="button" className={s.jl} onClick={() => toast({ msg: 'Chi tiết Job mở ở vùng Job' })}>{d.job}</button><span className={s.tx2}>{d.mbl}</span><span>{d.code}</span><span className={s.tx2}>{DOC_TYPE(d.type)}</span>
                              <span className={d.due === 'over' ? s.err : s.tx2}>{dueText(d)} · {d.status}</span><b className={s.r}>{f1(d.amount)}</b>
                            </div>
                          ))}
                        </div>
                      )}
                      {g.dups.map((d) => {
                        const x = PAID_ELSEWHERE[d.id]!;
                        const tip = `${x[0] === 'EMP' ? 'Nhân viên trả' : 'Công ty đã chi'} · ${x[3]} · chặn đưa vào đợt chi`;
                        return (
                          <div key={d.id} className={s.dup}>
                            <span className={s.dchip} title={tip} aria-label={tip}><IconFlag size={12} aria-hidden="true" />{`Đã trả bởi ${x[1]} · ${x[2]}`}</span>
                            <b className={s.link}>{d.id}</b>
                            <span className={s.tx2}>{d.job} · {d.code}</span>
                            <b className={s.strike}>{f1(d.amount)}</b>
                            <span className={s.fill} />
                            <button type="button" className={s.hist} aria-label={`Lịch sử thanh toán ${d.id}`} title="Lịch sử thanh toán" onClick={() => toast({ msg: 'Sổ thu chi chưa mở ở bản này' })}>
                              <IconHistory size={14} aria-hidden="true" />
                            </button>
                            <button type="button" className={s.mini} onClick={() => { setClosed(new Set([...closed, d.id])); toast({ msg: `Đã đóng phải trả ${d.id} · không chi lại` }); }}><IconLock size={14} aria-hidden="true" />{t('spy.11')}</button>
                          </div>
                        );
                      })}
                      {g.blocked && <div className={s.note}>{g.blockNote}</div>}
                    </div>
                  );
                })}
                <div className={s.foot}><span>{t('spy.12')}</span><span>Đã chọn {picked.length} đối tác · {f1(selAmt)} tr{colPicked.length ? ` · gồm ${colPicked.length} khoản thu hộ` : ''}</span></div>
              </section>
              <section className={s.card} aria-label="Thu hộ cần chuyển trả">
                <div className={s.bh}>
                  <h2>{t('spy.13')}</h2>
                  <span className={s.tag} title="Thu hộ (COL) · chuyển trả cho đối tác">COL</span>
                  <span className={s.cnt}>{colOpen.length}</span>
                  {colOver > 0 && <span className={`${s.dchip} ${s.dchip2}`}><IconFlag size={12} aria-hidden="true" />{colOver} quá hạn</span>}
                  <span className={s.fill} />
                  <button type="button" className={s.mini} onClick={() => setColSel(Object.fromEntries(colDueIds().map((i) => [i, true])))}><IconPointer size={14} aria-hidden="true" />{t('spy.14')}</button>
                </div>
                <div className={`${s.cg} ${s.cgh}`} aria-hidden="true"><span /><span>{t('spy.15')}</span><span>Job</span><span>{t('spy.16')}</span><span className={s.r}>{t('spy.10')}</span><span>{t('spy.17')}</span><span>{t('spy.18')}</span><span /></div>
                {COLLECTIONS.map((c) => {
                  const on = !!colSel[c.id] && colSelectable(c);
                  return (
                    <label key={c.id} className={`${s.cg} ${s.crow} ${on ? s.gon : ''}`}>
                      <input type="checkbox" aria-label={`Đưa ${c.id} vào đợt chi`} checked={on} disabled={!colSelectable(c)} onChange={() => setColSel({ ...colSel, [c.id]: !colSel[c.id] })} />
                      <span className={s.cn}><b>{c.code}</b><span>{c.name}</span></span>
                      <button type="button" className={s.jl} onClick={(e) => { e.preventDefault(); toast({ msg: 'Chi tiết Job mở ở vùng Job' }); }}>{c.job}</button>
                      <span className={s.tx2} title={c.id}>{c.item}</span>
                      <b className={s.r}>{f1(c.amount)}</b>
                      <span className={s.cdue}>
                        <span className={c.state === 'over' ? s.err : s.tx2}><IconAlarm size={13} aria-hidden="true" />{c.due}</span>
                        <span className={s.rule} title={c.rule === 'SOA' ? 'Đại lý: chuyển trả theo kỳ SOA' : 'Mặc định 7 ngày làm việc sau khi thu'}>{c.rule === 'SOA' ? 'Theo SOA' : '7 ngày LV'}</span>
                      </span>
                      <span title={c.state === 'sent' ? `Đã vào đợt chi${c.unc ? ` · ${c.unc}` : ''}` : `Hạn chuyển trả ${c.due}`}><span className={`${s.st} ${s[`st_${COL_TONE[c.state]}`]}`}>{colStatus(c)}</span></span>
                      <button type="button" className={s.hist} aria-label={`Lịch sử thanh toán ${c.id}`} title="Lịch sử thanh toán" onClick={(e) => { e.preventDefault(); toast({ msg: 'Sổ thu chi chưa mở ở bản này' }); }}>
                        <IconHistory size={14} aria-hidden="true" />
                      </button>
                    </label>
                  );
                })}
              </section>
            </>
          )}
        </StateView>
      </div>
    </Page>
  );
}
